import React, { useEffect, useState } from "react";
import {
  Search,
  RotateCw,
  SlidersHorizontal,
  ChevronDown,
  Eraser,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useDebounce } from "use-debounce";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const value = String(i + 1).padStart(2, "0");
  return { value, label: `Tháng ${value}` };
});

const EMPTY_FILTERS = { keyword: "", year: "", month: "" };

const MyPayslipFilter = ({ onFilterChange, onRefresh, yearOptions = [] }) => {
  const [advanced, setAdvanced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [debouncedKeyword] = useDebounce(filters.keyword, 400);
  const isFiltering = Object.values(filters).some((v) => v !== "");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    onFilterChange?.({ ...filters, keyword: debouncedKeyword });
  }, [debouncedKeyword]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateFilter = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    if (name !== "keyword") onFilterChange?.(updated);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    onFilterChange?.(EMPTY_FILTERS);
  };

  const ActionButton = ({ icon: Icon, onClick, title, active }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 ${
        active
          ? "bg-red-50 border-red-200 text-red-600 shadow-sm"
          : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
      }`}
    >
      <Icon size={18} />
    </button>
  );

  const SelectBox = ({ label, value, options, onChange }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <div className="relative group">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-10 rounded-2xl text-sm
                     focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all cursor-pointer font-medium shadow-sm"
        >
          <option value="">Tất cả {label.toLowerCase()}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-red-500 transition-colors"
        />
      </div>
    </div>
  );

  const advancedFields = (
    <>
      <SelectBox
        label="Năm"
        value={filters.year}
        options={yearOptions.map((y) => ({ value: y, label: y }))}
        onChange={(v) => updateFilter("year", v)}
      />
      <SelectBox
        label="Tháng"
        value={filters.month}
        options={MONTH_OPTIONS}
        onChange={(v) => updateFilter("month", v)}
      />
    </>
  );

  if (isMobile) {
    return (
      <div className="mb-6 space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-1 group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder="Tìm theo tháng lương..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-white border border-gray-200 pl-11 pr-4 py-3 rounded-2xl focus:outline-none focus:border-red-500 shadow-sm transition-all"
            />
          </div>
          <ActionButton
            icon={SlidersHorizontal}
            onClick={() => setAdvanced(!advanced)}
            active={advanced}
            title="Lọc nâng cao"
          />
        </div>

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title="Bộ lọc nâng cao"
          footer={
            <>
              <button
                type="button"
                onClick={clearFilters}
                className="py-4 border border-gray-200 text-gray-700 rounded-2xl font-bold flex-1"
              >
                XÓA LỌC
              </button>
              <button
                type="button"
                onClick={() => setAdvanced(false)}
                className="py-4 bg-red-600 text-white rounded-2xl font-bold shadow-lg shadow-red-200 flex-1"
              >
                ÁP DỤNG
              </button>
            </>
          }
        >
          <div className="grid grid-cols-1 gap-4">{advancedFields}</div>
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  return (
    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm mb-6">
      <div className="flex items-center gap-3">
        <div className="relative w-full max-w-sm group">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
            size={18}
          />
          <input
            type="text"
            placeholder="Tìm theo tháng lương, mã kỳ lương..."
            value={filters.keyword}
            onChange={(e) => updateFilter("keyword", e.target.value)}
            className="w-full bg-gray-50 border-transparent border-2 focus:bg-white focus:border-red-500 pl-11 pr-4 py-2.5 rounded-2xl outline-none transition-all font-medium text-gray-700"
          />
        </div>

        <ActionButton
          icon={RotateCw}
          onClick={() => (onRefresh ? onRefresh() : onFilterChange?.({ ...filters }))}
          title="Làm mới"
        />
        <ActionButton
          icon={SlidersHorizontal}
          onClick={() => setAdvanced(!advanced)}
          active={advanced}
          title="Lọc nâng cao"
        />

        {isFiltering && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider transition-all ml-2"
          >
            <Eraser size={14} /> Xóa lọc
          </button>
        )}
      </div>

      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-6 pt-6 border-t border-gray-100">
              {advancedFields}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MyPayslipFilter;
